import { moneyFormat } from "@redotech/money/util";
import { useHandler } from "@redotech/react-util/hook";
import { OrderValueSegment } from "@redotech/redo-model/post-purchase/post-purchase-flow";
import {
  PostPurchaseSegmentNumberOperator,
  postPurchaseSegmentNumberOperatorToFriendlyString,
  postPurchaseSegmentNumberOperatorToOperatorString,
} from "@redotech/redo-model/post-purchase/segment-types";
import { memo } from "react";
import { RedoTextInput } from "../../../arbiter-components/input/redo-text-input";
import { CurrencySymbolIcon } from "../../../currency-symbol-icon";
import { Flex } from "../../../flex";
import { Text } from "../../../text";
import { OperatorSelector } from "../../operator-selector";

interface OrderValueSegmentCommonProps {
  condition: OrderValueSegment;
}

export type OrderValueSegmentProps = OrderValueSegmentCommonProps &
  (
    | { readonly: false; setCondition: (condition: OrderValueSegment) => void }
    | { readonly: true }
  );

export const OrderValueSegmentCondition = memo(
  function OrderValueSegmentCondition({
    condition,
    ...props
  }: OrderValueSegmentProps) {
    const handleOperatorSelected = useHandler(
      (operator: PostPurchaseSegmentNumberOperator) => {
        if (!props.readonly) {
          props.setCondition({ ...condition, operator });
        }
      },
    );

    const handleAmountChange = useHandler((amount: string) => {
      if (!props.readonly) {
        props.setCondition({ ...condition, amount });
      }
    });

    return props.readonly ? (
      <Text fontSize="xs">
        Order value is{" "}
        {postPurchaseSegmentNumberOperatorToOperatorString[condition.operator]}{" "}
        {moneyFormat({
          amount: +condition.amount,
          currency: condition.currency,
        })}
      </Text>
    ) : (
      <>
        <Text fontSize="xs" textColor="primary">
          Order value is
        </Text>
        <OperatorSelector<PostPurchaseSegmentNumberOperator>
          availableOptions={Object.values(PostPurchaseSegmentNumberOperator)}
          onOperatorSelected={handleOperatorSelected}
          operatorToText={postPurchaseSegmentNumberOperatorToFriendlyString}
          readonly={props.readonly}
          selectedOperator={condition.operator}
        />
        <Flex maxW="full">
          <RedoTextInput
            IconLeading={() => (
              <CurrencySymbolIcon currency={condition.currency} />
            )}
            inputTypeHint="number"
            placeholder="0"
            setValue={handleAmountChange}
            size="sm"
            state={props.readonly ? "disabled" : "default"}
            value={condition.amount}
          />
        </Flex>
      </>
    );
  },
);
